import { useMemo, useState } from "react";
import {
  ConversionDelta,
  FunnelBars,
  PeriodFilter,
  useFunnelStats,
  useTenantPrimaryColor,
} from "../features/cardapio-funnel";
import { CarregandoBloco } from "../components/carregando";
import { Skeleton } from "../components/ui/skeleton";

/**
 * Página de Relatório do Cardápio — magra por design (regra de conventions.md:
 * páginas têm 50-150 LOC e só compõem features). Toda lógica visual/dados vive
 * em `features/cardapio-funnel/`.
 */
export default function RelatorioCardapio() {
  const [period, setPeriod] = useState("7d");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { stats, loading, carregou } = useFunnelStats({ period, from, to });
  const primaryHSL = useTenantPrimaryColor();

  const overallDelta = useMemo(() => calcOverallDelta(stats), [stats]);

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <Header />
      <PeriodFilter
        period={period}
        setPeriod={setPeriod}
        from={from}
        setFrom={setFrom}
        to={to}
        setTo={setTo}
      />
      {/* Carregamento com cards (25/10/2026): só na 1ª abertura; trocar o
          período depois mantém o funil e mostra "Atualizando…" no rodapé. */}
      {!carregou ? <EsqueletoFunil /> : (
        <div className={`space-y-6 transition-opacity duration-200 ${loading ? "opacity-60" : ""}`}>
          <ConversionDelta overallDelta={overallDelta} range={stats?.range} />
          <FunnelBars stats={stats} primaryHSL={primaryHSL} />
          <FooterStatus loading={loading} hasStats={!!stats} />
        </div>
      )}
    </div>
  );
}

function Header() {
  return (
    <div>
      <h1 className="text-3xl font-bold">Relatório do Cardápio</h1>
      <p className="text-muted-foreground mt-1">Funil de conversão das visitas ao pedido concluído</p>
    </div>
  );
}

// Faixa da comparação + as 5 barras do funil, cada uma mais estreita.
const LARGURAS_FUNIL = ["100%", "82%", "67%", "55%", "45%"];
function EsqueletoFunil() {
  return (
    <div className="space-y-6" data-e2e="esqueleto-funil">
      <CarregandoBloco altura="h-[88px]" />
      <div className="bg-card rounded-2xl border shadow-sm p-4 md:p-6 space-y-3" aria-busy="true" aria-label="Carregando">
        {LARGURAS_FUNIL.map((w) => (
          <div key={w} className="flex justify-center">
            <Skeleton className="h-[140px] rounded-2xl" style={{ width: w, minWidth: 260 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

function FooterStatus({ loading, hasStats }) {
  if (loading) {
    return <p className="text-xs text-muted-foreground text-center min-h-[1rem]">Atualizando…</p>;
  }
  if (!hasStats) {
    return (
      <p className="text-xs text-muted-foreground text-center min-h-[1rem]">
        Sem dados para o período selecionado.
      </p>
    );
  }
  return <div className="min-h-[1rem]" />;
}

function calcOverallDelta(stats) {
  if (!stats) return 0;
  const cur = stats.current || {};
  const prev = stats.previous || {};
  const convNow = cur.visit ? (cur.completed / cur.visit) * 100 : 0;
  const convPrev = prev.visit ? (prev.completed / prev.visit) * 100 : 0;
  if (!convPrev) return 0;
  return Number((((convNow - convPrev) / convPrev) * 100).toFixed(2));
}
